import React, { useState, useEffect } from "react";
import { Card } from "./ui/card";
import {
  Activity,
  AlertTriangle,
  Maximize2,
  X,
  CheckCircle2,
  Info,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Button } from "./ui/button";
import "./ModelDiagnostics.css";

const ModelDiagnostics = ({
  satelliteId,
  constellation,
  selectedSatellite,
  showTitle = true,
  isInModal = false,
  onExpandClick = null,
  onDragStateChange = null,
}) => {
  const [imageError, setImageError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  // const handleZoomIn = () => {
  //   setZoom((prev) => Math.min(prev + 0.2, 3));
  // };

  // const handleZoomOut = () => {
  //   setZoom((prev) => Math.max(prev - 0.2, 1));
  // };

  const handleZoomIn = () => {
    setZoom((prev) => {
      const isMobile = window.innerWidth <= 768;
      const maxZoom = isMobile ? 5 : 3;
      const increment = isMobile ? 0.5 : 0.2;
      const newZoom = Math.min(prev + increment, maxZoom);
      // console.log("Zoom In:", { prev, newZoom, maxZoom, isMobile });
      return newZoom;
    });
  };

  const handleZoomOut = () => {
    setZoom((prev) => {
      const isMobile = window.innerWidth <= 768;
      const decrement = isMobile ? 0.5 : 0.2;
      const newZoom = Math.max(prev - decrement, 1);
      // console.log("Zoom Out:", { prev, newZoom, isMobile });
      return newZoom;
    });
  };

  const resetZoom = () => {
    setZoom(1);
    setDragOffset({ x: 0, y: 0 });
  };

  // Desktop Mouse Events (only used for initial mousedown)
  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  };

  // Mobile Touch Events (only used for initial touchstart)
  const handleTouchStart = (e) => {
    if (zoom > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - dragOffset.x,
        y: e.touches[0].clientY - dragOffset.y,
      });
    }
  };

  const handleExpandClick = () => {
    if (onExpandClick) {
      onExpandClick();
    } else {
      setIsExpanded(true);
      setZoom(1);
    }
  };

  const diagnosticInfo = [
    {
      title: "Shapiro-Wilk Test",
      description:
        "Statistical test for normality of residuals. p > 0.05 indicates normal distribution.",
      color: "rgba(34, 197, 94, 0.1)",
      borderColor: "rgba(34, 197, 94, 0.3)",
      icon: CheckCircle2,
    },
    {
      title: "Q-Q Plot",
      description:
        "Visual check comparing residual distribution to theoretical normal distribution.",
      color: "rgba(59, 130, 246, 0.1)",
      borderColor: "rgba(59, 130, 246, 0.3)",
      icon: Info,
    },
  ];

  // Notify parent component about drag state changes
  useEffect(() => {
    if (onDragStateChange) {
      onDragStateChange(isDragging);
    }
  }, [isDragging, onDragStateChange]);

  // Document-level mouse event handlers for smooth dragging
  useEffect(() => {
    if (!isInModal) return;

    const handleDocumentMouseMove = (e) => {
      if (isDragging && zoom > 1) {
        e.preventDefault();
        setDragOffset({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    };

    const handleDocumentMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleDocumentMouseMove);
      document.addEventListener("mouseup", handleDocumentMouseUp);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    } else {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleDocumentMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isInModal, isDragging, zoom, dragStart.x, dragStart.y]);

  // Document-level touch event handlers for mobile
  useEffect(() => {
    if (!isInModal) return;

    const handleDocumentTouchMove = (e) => {
      if (isDragging && zoom > 1 && e.touches.length === 1) {
        e.preventDefault();
        e.stopPropagation();
        setDragOffset({
          x: e.touches[0].clientX - dragStart.x,
          y: e.touches[0].clientY - dragStart.y,
        });
      }
    };

    const handleDocumentTouchEnd = (e) => {
      if (isDragging) {
        e.preventDefault();
        setIsDragging(false);
      }
    };

    if (isDragging) {
      document.addEventListener("touchmove", handleDocumentTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleDocumentTouchEnd, {
        passive: false,
      });
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.removeEventListener("touchmove", handleDocumentTouchMove);
      document.removeEventListener("touchend", handleDocumentTouchEnd);
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isInModal, isDragging, zoom, dragStart.x, dragStart.y]);

  // Modal mode - just image viewer with zoom
  if (isInModal) {
    return (
      <div
        style={{
          background: "rgba(255, 255, 255, 0.03)",
          borderRadius: "8px",
          padding: "16px",
          flex: 1,
          minHeight: "400px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          overflow: "hidden",
          touchAction: zoom > 1 ? "none" : "auto",
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* Zoom Controls */}
        <div
          className="diagnostics-zoom-controls"
          style={{
            position: "absolute",
            top: "0px",
            left: "16px",
            display: "flex",
            gap: "8px",
            zIndex: 10,
            background: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(8px)",
            padding: "8px 12px",
            borderRadius: "8px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleZoomIn();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="zoom-btn zoom-in-btn"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "12px",
              padding: "4px 8px",
            }}
          >
            <ZoomIn size={14} />
            Zoom In
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleZoomOut();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="zoom-btn zoom-out-btn"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "12px",
              padding: "4px 8px",
            }}
          >
            <ZoomOut size={14} />
            Zoom Out
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              resetZoom();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="zoom-btn"
            style={{
              fontSize: "12px",
              padding: "4px 8px",
            }}
          >
            Reset
          </Button>
          <div
            className="zoom-percentage"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              padding: "0 12px",
            }}
          >
            {Math.round(zoom * 100)}%
          </div>
        </div>

        {/* Image Display */}
        {!imageError ? (
          <img
            src={require(`../assets/diagnostics/${selectedSatellite.satellite_id}_residuals.png`)}
            alt={`Residual Distribution and Q-Q Plot for ${satelliteId}`}
            style={{
              maxWidth: "90%",
              height: "auto",
              maxHeight: "85%",
              marginTop: "50px",
              borderRadius: "8px",
              objectFit: "contain",
              transform: `scale(${zoom}) translate(${dragOffset.x / zoom}px, ${
                dragOffset.y / zoom
              }px)`,
              transition: isDragging ? "none" : "transform 0.2s ease",
              userSelect: "none",
              draggable: false,
              pointerEvents: "none",
              touchAction: "none",
            }}
            onError={handleImageError}
            draggable={false}
          />
        ) : (
          <div
            style={{
              padding: "60px",
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.5)",
            }}
          >
            <AlertTriangle size={32} style={{ marginBottom: "16px" }} />
            <div>Diagnostic plots could not be loaded</div>
          </div>
        )}
      </div>
    );
  }

  // Card view for right panel (unchanged)
  return (
    <>
      <Card className="diagnostics-card" style={{ marginTop: "20px" }}>
        <div style={{ padding: "16px" }}>
          {showTitle && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#fff",
                  }}
                >
                  <Activity size={18} />
                  Model Diagnostics
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleExpandClick}
                  style={{
                    padding: "4px 8px",
                    height: "auto",
                    fontSize: "12px",
                  }}
                >
                  <Maximize2 size={14} />
                </Button>
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "rgba(255, 255, 255, 0.7)",
                  marginBottom: "16px",
                }}
              >
                Statistical validation of prediction model for {satelliteId}
              </p>
            </>
          )}

          <div
            style={{
              display: "grid",
              gap: "12px",
              gridTemplateColumns: "1fr",
            }}
          >
            {/* Image Container - constrained height */}
            <div
              style={{
                position: "relative",
                background: "rgba(255, 255, 255, 0.05)",
                borderRadius: "8px",
                padding: "12px",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                minHeight: "200px",
                maxHeight: "280px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {!imageError && (
                <>
                  {!imageLoaded && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "rgba(255, 255, 255, 0.5)",
                        fontSize: "13px",
                        textAlign: "center",
                      }}
                    >
                      <Activity
                        size={24}
                        style={{
                          animation: "spin 2s linear infinite",
                          marginBottom: "8px",
                        }}
                      />
                      <div>Loading diagnostics...</div>
                    </div>
                  )}
                  <img
                    src={require(`../assets/diagnostics/${selectedSatellite.satellite_id}_residuals.png`)}
                    alt={`Residual Distribution and Q-Q Plot for ${satelliteId}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: "4px",
                      display: imageLoaded ? "block" : "none",
                      cursor: "pointer",
                    }}
                    onError={handleImageError}
                    onLoad={handleImageLoad}
                    onClick={handleExpandClick}
                    title="Click to expand"
                  />
                </>
              )}

              {imageError && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "40px 20px",
                    color: "rgba(255, 255, 255, 0.5)",
                    fontSize: "13px",
                    textAlign: "center",
                    gap: "12px",
                  }}
                >
                  <AlertTriangle size={24} />
                  <div>
                    <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                      Diagnostic plots not available
                    </div>
                    <div style={{ fontSize: "12px", opacity: 0.7 }}>
                      Residual analysis for {satelliteId} could not be loaded
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Info Badges */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "8px",
                fontSize: "12px",
                color: "rgba(255, 255, 255, 0.9)",
              }}
            >
              {diagnosticInfo.map((info, index) => {
                const IconComponent = info.icon;
                return (
                  <div
                    key={index}
                    style={{
                      background: info.color,
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: `1px solid ${info.borderColor}`,
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.borderColor =
                        info.borderColor.replace("0.3", "0.5");
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderColor = info.borderColor;
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontWeight: "600",
                        marginBottom: "6px",
                      }}
                    >
                      <IconComponent size={14} />
                      {info.title}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        opacity: 0.8,
                        lineHeight: "1.4",
                      }}
                    >
                      {info.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

export default ModelDiagnostics;
